/**
 * StepHealth - Étape 2 : Profil santé & précautions
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, Shield, ChevronRight } from 'lucide-react';

export function StepHealth({ profile, onUpdate, onNext, onSkip }) {
  const [answers, setAnswers] = useState({
    hasMedication: profile.hasMedication || false,
    medicationDetails: profile.medicationDetails || '',
    hasCondition: profile.hasCondition || false,
    hasRecentCheckup: profile.hasRecentCheckup || 'no',
    hasTDAHMed: profile.medicationType === 'TDAH_stimulant' ? 'yes' : 'no',
    disclaimerAccepted: profile.disclaimerAccepted || false,
  });

  const update = (key, value) => {
    setAnswers(prev => ({ ...prev, [key]: value }));
  };

  const handleSubmit = () => {
    onUpdate({
      hasMedication: answers.hasMedication,
      medicationDetails: answers.medicationDetails,
      hasCondition: answers.hasCondition,
      hasRecentCheckup: answers.hasRecentCheckup,
      medicationType: answers.hasTDAHMed === 'yes' ? 'TDAH_stimulant' : null,
      hasHypertension: answers.hasCondition,
      disclaimerAccepted: answers.disclaimerAccepted,
    });
    onNext();
  };

  const RadioOption = ({ label, selected, onClick }) => (
    <button
      onClick={onClick}
      className={`px-4 py-2.5 rounded-xl border text-sm font-medium transition-all
        ${selected
          ? 'bg-orange-500/20 border-orange-500/50 text-orange-300'
          : 'bg-zinc-800/60 border-zinc-700/50 text-zinc-400 hover:border-zinc-600'
        }`}
    >
      {label}
    </button>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="px-4 py-4 space-y-5"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-bold text-white">Profil santé</h3>
        <button
          onClick={onSkip}
          className="text-sm text-zinc-500 hover:text-zinc-300"
        >
          Passer &rarr;
        </button>
      </div>

      {/* Questions */}
      <div className="space-y-4">
        {/* Traitement médical */}
        <div className="space-y-2">
          <p className="text-sm font-medium text-zinc-300">Traitement médical en cours ?</p>
          <div className="flex gap-2">
            <RadioOption label="Oui" selected={answers.hasMedication} onClick={() => update('hasMedication', true)} />
            <RadioOption label="Non" selected={!answers.hasMedication} onClick={() => update('hasMedication', false)} />
          </div>
          {answers.hasMedication && (
            <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }}>
              <input
                type="text"
                placeholder="Précisez (optionnel)..."
                value={answers.medicationDetails}
                onChange={(e) => update('medicationDetails', e.target.value)}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg
                  text-zinc-300 text-sm placeholder-zinc-600 focus:border-orange-500/50 focus:outline-none"
              />
              <p className="text-xs text-amber-400 mt-1 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                Consultez votre médecin avant tout programme sportif intensif
              </p>
            </motion.div>
          )}
        </div>

        {/* Conditions */}
        <div className="space-y-2">
          <p className="text-sm font-medium text-zinc-300">
            Diabète, hypertension, problème cardiaque ?
          </p>
          <div className="flex gap-2">
            <RadioOption label="Oui" selected={answers.hasCondition} onClick={() => update('hasCondition', true)} />
            <RadioOption label="Non" selected={!answers.hasCondition} onClick={() => update('hasCondition', false)} />
          </div>
        </div>

        {/* Bilan santé */}
        <div className="space-y-2">
          <p className="text-sm font-medium text-zinc-300">Dernier bilan de santé &lt; 1 an ?</p>
          <div className="flex gap-2">
            <RadioOption label="Oui" selected={answers.hasRecentCheckup === 'yes'} onClick={() => update('hasRecentCheckup', 'yes')} />
            <RadioOption label="Non" selected={answers.hasRecentCheckup === 'no'} onClick={() => update('hasRecentCheckup', 'no')} />
            <RadioOption label="Jamais" selected={answers.hasRecentCheckup === 'never'} onClick={() => update('hasRecentCheckup', 'never')} />
          </div>
        </div>

        {/* TDAH médication */}
        <div className="space-y-2">
          <p className="text-sm font-medium text-zinc-300">TDAH avec traitement (Ritaline, Concerta...) ?</p>
          <div className="flex gap-2">
            <RadioOption label="Oui" selected={answers.hasTDAHMed === 'yes'} onClick={() => update('hasTDAHMed', 'yes')} />
            <RadioOption label="Non" selected={answers.hasTDAHMed === 'no'} onClick={() => update('hasTDAHMed', 'no')} />
            <RadioOption label="Préfère pas dire" selected={answers.hasTDAHMed === 'skip'} onClick={() => update('hasTDAHMed', 'skip')} />
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 space-y-3">
        <div className="flex items-start gap-2">
          <Shield className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
          <p className="text-xs text-amber-300/80">
            ADHDManager ne remplace pas un avis médical. Les recommandations proposées sont
            à titre informatif. Consultez votre médecin avant de commencer tout programme sportif intensif.
          </p>
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={answers.disclaimerAccepted}
            onChange={(e) => update('disclaimerAccepted', e.target.checked)}
            className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-orange-500
              focus:ring-orange-500/30"
          />
          <span className="text-sm text-zinc-300">J'ai lu et j'accepte</span>
        </label>
      </div>

      {/* Submit */}
      <button
        onClick={handleSubmit}
        disabled={!answers.disclaimerAccepted}
        className="w-full py-3.5 rounded-xl bg-gradient-to-r from-orange-500 to-red-500
          text-white font-bold flex items-center justify-center gap-2
          disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
      >
        Continuer <ChevronRight className="w-5 h-5" />
      </button>
    </motion.div>
  );
}
